import React, { useRef, useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Modal,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  OutlinedInput,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MovieEditIcon from "@mui/icons-material/MovieEdit";
import { Snackbar, Alert } from "@mui/material";
import "./VideoUpload.css";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadVideosWithMetadata,
  updateVideosWithMetadata,
  resetStatus,
  fetchVideos,
} from "../../Redux/VideoUpload";
import { IgniteAdmins } from "./IgniteAdmins";
import EditIcon from "@mui/icons-material/Edit";
import axios from "axios";
import { BASE_API_URL } from "utils/constants";

const CARD_WIDTH = 280;
const GAP = 24;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: "background.paper",
  border: "0.1px solid #000",
  boxShadow: 24,
  borderRadius: "5px",
  overflow: "auto",
  height: "80%",
  width: "90%",
};

const OtherTalksSection = ({
  talks = [],
  activeTalkId,
  onSelectTalk,
  imageUrls,
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error
  });
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  let loggedInEnterpriseId = "";
  if (activeAccount)
    loggedInEnterpriseId = activeAccount.idTokenClaims.enterpriseID;
  const isAdmin = IgniteAdmins.includes(loggedInEnterpriseId);

  // Video upload
  const [videos, setVideos] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [editingTalkId, setEditingTalkId] = useState(null);
  const [editingClipIndex, setEditingClipIndex] = useState(null);
  const [selectedClipIndex, setSelectedClipIndex] = useState(0);
  const categoryScrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const scrollCategoriesRight = () => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({
        left: 250,
        behavior: "smooth",
      });
    }
  };

  const scrollCategoriesLeft = () => {
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollBy({
        left: -250,
        behavior: "smooth",
      });
    }
  };

  const updateCategoryArrows = () => {
    const container = categoryScrollRef.current;

    if (!container) return;

    setShowLeftArrow(container.scrollLeft > 0);

    setShowRightArrow(
      container.scrollLeft <
      container.scrollWidth - container.clientWidth - 5
    );
  };



  const [formData, setFormData] = useState({
    speakerName: "",
    sessionTitle: "",
    sessionDescription: "",
    sessionDuration: "",
    totalRecordings: "",
    sessionDate: "",
    clipTitle: "",
    clipDescription: "",
    speakerImage: null,
    category: "",
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageDragging, setImageDragging] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTalkId, setSelectedTalkId] = useState(null);
  const [currentClip, setCurrentClip] =
    useState({
      file: null,
      duration: null,
      autoThumbnail: null,
      customThumbnail: null,
      customThumbnailFile: null,
      clipTitle: "",
      clipDescription: "",
      videoUrl: "",
      thumbnailUrl: "",
    });
  const latestVideo = videos[videos.length - 1];

  const handleUpload = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = null;

    const validVideos = files.filter((file) =>
      file.type.startsWith("video/")
    );

    if (!validVideos.length) return;

    // This is a new clip, not an edit of the selected clip
    setEditingClipIndex(null);
    setSelectedClipIndex(-1);

    const file = validVideos[0];

    const url = URL.createObjectURL(file);
    const videoElement = document.createElement("video");

    videoElement.preload = "metadata";
    videoElement.src = url;

    videoElement.onloadedmetadata = () => {
      const duration = videoElement.duration;

      setCurrentClip((prev) => ({
        ...prev,
        file,
        duration,
        autoThumbnail: null,
      }));

      videoElement.currentTime = 0.1;

      videoElement.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(videoElement, 0, 0);

          const thumbnail = canvas.toDataURL("image/png");

          setCurrentClip((prev) => ({
            ...prev,
            autoThumbnail: thumbnail,
          }));
        } catch (err) {
          console.log("Thumbnail failed:", err);
        }

        URL.revokeObjectURL(url);
      };
    };
  };

  const handleRemove = (index) => {
    const updated = [...videos];
    updated.splice(index, 1);
    setVideos(updated);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validVideos = files.filter((f) => f.type.startsWith("video/"));

    if (!validVideos.length) return;

    const file = validVideos[0];

    const url = URL.createObjectURL(file);
    const videoElement = document.createElement("video");

    videoElement.preload = "metadata";
    videoElement.src = url;

    videoElement.onloadedmetadata = () => {
      const duration = videoElement.duration;

      // overwrite state
      setCurrentClip((prev) => ({
        ...prev,
        file,
        duration,
        autoThumbnail: null,
      }));

      videoElement.currentTime = 0.1;

      videoElement.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(videoElement, 0, 0);

          const thumbnail = canvas.toDataURL("image/png");

          setCurrentClip((prev) => {
            if (prev.customThumbnail) return prev;

            return {
              ...prev,
              autoThumbnail: thumbnail,
            };
          });
        } catch (err) {
          console.log("Thumbnail failed");
        }

        URL.revokeObjectURL(url);
      };
    };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (file) => {
    if (!file || !file.type.startsWith("image/")) return;

    const previewUrl = URL.createObjectURL(file);

    setFormData((prev) => ({
      ...prev,
      speakerImage: file,
    }));

    setImagePreview(previewUrl);
  };

  const dispatch = useDispatch();

  const handleSubmit = () => {
    const formattedVideos = videos.map((v) => ({
      id: v.id,
      clipTitle: v.clipTitle,
      clipDescription: v.clipDescription,
    }));

    if (!formData.speakerName) {
      alert("Speaker name is required");
      return;
    }

    if (!editingTalkId && !videos.length) {
      alert("Please add at least one video");
      return;
    }

    if (editingTalkId) {
      dispatch(
        updateVideosWithMetadata({
          sessionId: editingTalkId,
          formData,
          videos: formattedVideos,
        })
      );
    } else {
      dispatch(
        uploadVideosWithMetadata({
          formData,
          videos,
        })
      );
    }
  };

  const uploadDraftVideoToBlob = async (
    file,
    blobPath
  ) => {

    const { data } = await axios.get(
      `${BASE_API_URL}/generateSasToken`,
      {
        params: {
          filename: blobPath,
        },
        headers: {
          Authorization:
            localStorage.getItem("token"),
        },
      }
    );

    await axios.put(
      data.sasUrl,
      file,
      {
        headers: {
          "x-ms-blob-type":
            "BlockBlob",
          "Content-Type":
            file.type,
        },
      }
    );

    return data.sasUrl.split("?")[0];
  };

  const base64ThumbnailToFile = (
    base64String,
    fileName
  ) => {
    const [header, base64Data] =
      base64String.split(",");

    const mimeType =
      header.match(/:(.*?);/)?.[1] ||
      "image/png";

    const binaryString =
      window.atob(base64Data);

    const bytes =
      new Uint8Array(binaryString.length);

    for (
      let index = 0;
      index < binaryString.length;
      index++
    ) {
      bytes[index] =
        binaryString.charCodeAt(index);
    }

    return new File(
      [bytes],
      fileName,
      {
        type: mimeType,
      }
    );
  };

  const handleSaveDraft = async () => {

    if (!formData.speakerName?.trim()) {
      setSnackbar({
        open: true,
        message: "Speaker name is required",
        severity: "error",
      });
      return;
    }

    if (!currentClip.clipTitle?.trim()) {
      setSnackbar({
        open: true,
        message: "Clip title is required",
        severity: "error",
      });

      return;
    }

    try {

      setUploading(true);

      const previousClip =
        editingClipIndex !== null
          ? videos[editingClipIndex]
          : null;

      let uploadedVideoUrl =
        currentClip.videoUrl ||
        previousClip?.videoUrl ||
        "";

      let uploadedThumbnailUrl =
        currentClip.thumbnailUrl ||
        previousClip?.thumbnailUrl ||
        "";

      if (
        currentClip.file &&
        !uploadedVideoUrl
      ) {

        const cleanSpeaker =
          formData.speakerName
            ?.toLowerCase()
            ?.replace(/\s+/g, "_");

        const cleanCategory =
          formData.category
            ?.toLowerCase()
            ?.replace(/\s+/g, "_");

        const originalName =
          currentClip.file.name;

        const blobPath =
          `igniteVideos/${cleanCategory}/${cleanSpeaker}/${originalName}`;

        uploadedVideoUrl =
          await uploadDraftVideoToBlob(
            currentClip.file,
            blobPath
          );

        // uploadedVideoUrl = "https://testblob/temporary-video.mp4";
      }

      if (
        (currentClip.customThumbnail ||
          currentClip.autoThumbnail) &&
        !uploadedThumbnailUrl
      ) {

        let thumbnailFile;

        if (currentClip.customThumbnailFile) {

          thumbnailFile =
            currentClip.customThumbnailFile;

        } else if (currentClip.autoThumbnail) {

          thumbnailFile =
            base64ThumbnailToFile(
              currentClip.autoThumbnail,
              "thumbnail.png"
            );
        }

        if (thumbnailFile) {

          const cleanSpeaker =
            formData.speakerName
              ?.toLowerCase()
              ?.replace(/\s+/g, "_");

          const cleanCategory =
            formData.category
              ?.toLowerCase()
              ?.replace(/\s+/g, "_");

          const originalName =
            currentClip.file?.name || `video_${Date.now()}.mp4`;

          const fileBaseName =
            originalName.replace(/\.[^/.]+$/, "");

          const thumbnailBlobPath =
            `igniteVideos/${cleanCategory}/${cleanSpeaker}/thumb_${fileBaseName}.png`;

          uploadedThumbnailUrl =
            await uploadDraftVideoToBlob(
              thumbnailFile,
              thumbnailBlobPath
            );

          // uploadedThumbnailUrl = "https://testblob/temporary-thumbnail.png";
        }
      }

      let uploadedSpeakerImageUrl =
        imagePreview?.startsWith("http")
          ? imagePreview
          : "";

      if (formData.speakerImage instanceof File) {
        const sanitize = (text = "") =>
          text
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "");

        const cleanSpeaker =
          sanitize(formData.speakerName) ||
          "speaker";

        const cleanCategory =
          sanitize(formData.category) ||
          "category";

        const imageExtension =
          formData.speakerImage.name
            ?.split(".")
            .pop()
            ?.toLowerCase() ||
          "png";

        const speakerImageBlobPath =
          `igniteVideos/${cleanCategory}/` +
          `${cleanSpeaker}/` +
          `speaker_${cleanSpeaker}.${imageExtension}`;

        uploadedSpeakerImageUrl =
          await uploadDraftVideoToBlob(
            formData.speakerImage,
            speakerImageBlobPath
          );

        // uploadedSpeakerImageUrl = "https://testblob/speaker-image.png";
      }

      const clipData = {
        ...previousClip,

        file:
          currentClip.file ||
          previousClip?.file ||
          null,

        duration:
          currentClip.duration ??
          previousClip?.duration ??
          0,

        clipTitle:
          currentClip.clipTitle.trim(),

        clipDescription:
          currentClip.clipDescription?.trim() || "",

        videoUrl:
          uploadedVideoUrl,

        thumbnailUrl:
          uploadedThumbnailUrl,

        thumbnail:
          currentClip.customThumbnail ||
          currentClip.autoThumbnail ||
          previousClip?.thumbnail ||
          null,

        thumbnailFile:
          currentClip.customThumbnailFile ||
          previousClip?.thumbnailFile ||
          null,
      };

      let updatedVideos = [];

      if (editingClipIndex !== null) {
        updatedVideos = videos.map((video, index) =>
          index === editingClipIndex
            ? clipData
            : video
        );
      } else {
        updatedVideos = [
          ...videos,
          clipData,
        ];
      }

      setVideos(updatedVideos);

      let draftVideos = [...updatedVideos];

      const payload = {
        sessionId: editingTalkId,
        createdBy: loggedInEnterpriseId,
        speakerName: formData.speakerName,
        sessionTitle: formData.sessionTitle,
        sessionDescription: formData.sessionDescription,
        category: formData.category,
        sessionDuration: formData.sessionDuration,
        totalRecordings:
          formData.totalRecordings ||
          draftVideos.length,
        sessionDate:
          formData.sessionDate || null,

        speakerImageUrl: uploadedSpeakerImageUrl || null,

        clips: draftVideos.map((v) => ({
          id: v.id,
          clipTitle: v.clipTitle,
          clipDescription: v.clipDescription,
          videoUrl: v.videoUrl || "",
          thumbnailUrl:
            v.thumbnailUrl ||
            v.thumbnail ||
            "",
          duration: v.duration,
        }))
      };

      const response = await axios.post(
        `${BASE_API_URL}/saveDraft`,
        payload,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      if (uploadedSpeakerImageUrl) {
        setImagePreview(
          uploadedSpeakerImageUrl
        );

        setFormData((prev) => ({
          ...prev,
          speakerImage: null,
        }));
      }

      setSnackbar({
        open: true,
        message: response.data.message,
        severity: "success",
      });

      setCurrentClip({
        file: null,
        duration: null,
        autoThumbnail: null,
        customThumbnail: null,
        customThumbnailFile: null,
        clipTitle: "",
        clipDescription: "",
        videoUrl: "",
        thumbnailUrl: "",
      });

      setEditingClipIndex(null);
      setSelectedClipIndex(-1);

    } catch (err) {

      setSnackbar({
        open: true,
        message:
          err.response?.data?.message ||
          "Failed to save draft",
        severity: "error",
      });

    } finally {
      setUploading(false);
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageDragging(false);

    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  const handleImageDragEnter = () => setImageDragging(true);
  const handleImageDragLeave = () => setImageDragging(false);
  const handleEdit = (talk) => {
    setEditingTalkId(talk.id);
    setSelectedClipIndex(0);

    setFormData({
      speakerName: talk?.speakerName || "",
      sessionTitle: talk?.title || "",
      sessionDescription: talk?.courseDescription || "",
      sessionDuration: talk?.duration || "",
      totalRecordings: talk?.episodes?.length || "",
      sessionDate: talk?.courseDate
        ? new Date(talk.courseDate).toISOString().split("T")[0]
        : "",
      speakerImage: null,
      category: talk?.category || "",
    });

    const speakerImage =
      !talk?.speakerImage
        ? null
        : talk.speakerImage.startsWith("http") ||
          talk.speakerImage.startsWith("data:")
          ? talk.speakerImage
          : imageUrls?.[talk.speakerImage];

    setImagePreview(speakerImage);

    const mappedVideos =
      talk?.episodes?.map((episode) => ({
        id: episode?.id,
        clipTitle: episode?.title || "",
        clipDescription: episode?.description || "",
        duration: episode?.duration || 0,
        videoUrl: episode?.url || null,
        fileName: episode?.fileName || "",
        thumbnail:
          episode?.thumbnail?.startsWith("http") ||
            episode?.thumbnail?.startsWith("data:")
            ? episode?.thumbnail
            : imageUrls?.[episode?.thumbnail],
      })) || [];

    setVideos(mappedVideos);

    const firstEpisode = talk?.episodes?.[0];

    setCurrentClip({
      file: null,
      duration: firstEpisode?.duration || null,
      autoThumbnail: null,
      customThumbnail:
        firstEpisode?.thumbnail?.startsWith("http") ||
          firstEpisode?.thumbnail?.startsWith("data:")
          ? firstEpisode?.thumbnail
          : imageUrls?.[firstEpisode?.thumbnail] || null,
      customThumbnailFile: null,
      clipTitle: firstEpisode?.title || "",
      clipDescription: firstEpisode?.description || "",
    });

    setOpen(true);
  };

  useEffect(() => {
    dispatch(fetchVideos());
  }, [dispatch]);

  const { loader, error, success } = useSelector((state) => state.VideoUpload);

  // Dynamic filter state
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  // Dynamic categories from talks
  const categories = ["ALL", ...new Set(talks.map((t) => t.category))];

  // const otherTalks = talks.filter((t) =>
  //   selectedCategory === "ALL" ? true : t.category === selectedCategory
  // );

  const filteredTalks = talks.filter((t) =>
    selectedCategory === "ALL" ? true : t.category === selectedCategory
  );



  useEffect(() => {
    updateCategoryArrows();

    const container = categoryScrollRef.current;

    if (!container) return;

    container.addEventListener("scroll", updateCategoryArrows);

    window.addEventListener("resize", updateCategoryArrows);

    return () => {
      container.removeEventListener(
        "scroll",
        updateCategoryArrows
      );

      window.removeEventListener(
        "resize",
        updateCategoryArrows
      );
    };
  }, [categories]);


  const otherTalks =
    selectedCategory === "ALL"
      ? filteredTalks.sort((a, b) => {
        const dateA = a.courseDate ? new Date(a.courseDate).getTime() : 0;
        const dateB = b.courseDate ? new Date(b.courseDate).getTime() : 0;
        return dateB - dateA;
      })
      : filteredTalks;

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    if (success) {
      setSnackbar({
        open: true,
        message: success,
        severity: "success",
      });

      setTimeout(async () => {
        try {
        await axios.delete(
          `${BASE_API_URL}/draft`,
          {
            headers: {
              Authorization:
                localStorage.getItem("token"),
            },
          }
        );
      } catch(err) {
        console.err("Delete draft failed", err)
      }
        setFormData({
          speakerName: "",
          sessionTitle: "",
          sessionDescription: "",
          sessionDuration: "",
          totalRecordings: "",
          sessionDate: "",
          clipTitle: "",
          clipDescription: "",
          speakerImage: null,
          category: "",
        });

        setVideos([]);
        setImagePreview(null);
        setOpen(false);
        setEditingTalkId(null);

        dispatch(fetchVideos());
        dispatch(resetStatus());
      }, 800);
    }

    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: "error",
      });

      dispatch(resetStatus());
    }
  }, [success, error, dispatch]);

  const ITEMS_PER_PAGE = 4;
  const pageCount = Math.ceil(otherTalks.length / ITEMS_PER_PAGE);

  const paginatedTalks = otherTalks.slice(
    activePage * ITEMS_PER_PAGE,
    (activePage + 1) * ITEMS_PER_PAGE
  );

  const previewThumbnail =
    currentClip.customThumbnail || currentClip.autoThumbnail;

  const [speakerImageDragging, setSpeakerImageDragging] = useState(false);
  const [thumbnailDragging, setThumbnailDragging] = useState(false);

  const handleSpeakerImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSpeakerImageDragging(false);

    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const handleThumbnailDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setThumbnailDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith("image/")) return;

    const previewUrl = URL.createObjectURL(file);

    setCurrentClip((prev) => ({
      ...prev,
      customThumbnail: previewUrl,
      customThumbnailFile: file,
    }));
  };

  const handleSpeakerDragEnter = () => setSpeakerImageDragging(true);
  const handleSpeakerDragLeave = () => setSpeakerImageDragging(false);

  const handleThumbnailDragEnter = () => setThumbnailDragging(true);
  const handleThumbnailDragLeave = () => setThumbnailDragging(false);

  const handleCloseModal = () => {
    setFormData({
      speakerName: "",
      sessionTitle: "",
      sessionDescription: "",
      sessionDuration: "",
      totalRecordings: "",
      sessionDate: "",
      clipTitle: "",
      clipDescription: "",
      speakerImage: null,
      category: "",
    });

    setVideos([]);
    setImagePreview(null);

    setCurrentClip({
      file: null,
      duration: null,
      autoThumbnail: null,
      customThumbnail: null,
      customThumbnailFile: null,
      clipTitle: "",
      clipDescription: "",
      videoUrl: "",
      thumbnailUrl: "",
    });

    setEditingTalkId(null);
    setOpen(false);
    setEditingClipIndex(null);
    setSelectedClipIndex(0);
  };

  const handleDeleteClick = (talkId) => {
    setSelectedTalkId(talkId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteTalk = async (talkId) => {
    try {
      const response = await axios.delete(
        `${BASE_API_URL}/videos/${talkId}`,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        }
      );

      setSnackbar({
        open: true,
        message: response.data.message || "Talk deleted successfully",
        severity: "success",
      });

      dispatch(fetchVideos());
    } catch (err) {
      console.log("FULL ERROR:", err);
      console.log("RESPONSE:", err.response?.data);

      setSnackbar({
        open: true,
        message:
          err.response?.data?.message ||
          "Failed to delete talk",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedTalkId(null);
    }
  };

  const handleUploadModalOpen = async () => {

    try {

      const response = await axios.get(
        `${BASE_API_URL}/draft`,
        {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        }
      );


      const data = response.data;

      if (data?.hasDraft) {

        const draft = data.draft;
        const clips = data.clips || [];

        setFormData({
          speakerName:
            draft.speaker_name || "",

          sessionTitle:
            draft.session_title || "",

          sessionDescription:
            draft.session_description || "",

          sessionDuration:
            draft.session_duration || "",

          totalRecordings:
            draft.total_recordings || "",

          sessionDate:
            draft.session_date
              ? new Date(
                draft.session_date
              )
                .toISOString()
                .split("T")[0]
              : "",

          clipTitle: "",
          clipDescription: "",
          speakerImage: null,

          category:
            draft.category || "",
        });

        setImagePreview(
          draft.speaker_image_url || null
        );

        const mappedVideos =
          clips.map((clip) => ({
            id: clip.id,

            clipTitle:
              clip.clip_title || "",

            clipDescription:
              clip.clip_description || "",

            videoUrl:
              clip.video_url || "",

            thumbnailUrl:
              clip.thumbnail_url || "",

            thumbnail:
              clip.thumbnail_url || "",

            duration:
              Number(clip.duration) || 0,

            file: null,
            thumbnailFile: null,
          }));

        setVideos(mappedVideos);

        setEditingClipIndex(
          mappedVideos.length ? 0 : null
        );

        setSelectedClipIndex(0);

        if (!mappedVideos.length) {
          setCurrentClip({
            file: null,
            duration: null,
            autoThumbnail: null,
            customThumbnail: null,
            customThumbnailFile: null,
            clipTitle: "",
            clipDescription: "",
            videoUrl: "",
          });
        }

        if (mappedVideos.length) {

          setEditingClipIndex(null);

          setSelectedClipIndex(-1);

          setCurrentClip({
            file: null,
            duration: null,
            autoThumbnail: null,
            customThumbnail: null,
            customThumbnailFile: null,
            clipTitle: "",
            clipDescription: "",
            videoUrl: "",
            thumbnailUrl: "",
          });

        }
      }

      setOpen(true);

    } catch (err) {

      console.error(err);

      setOpen(true);

    }
  };

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 2,
        }}
        className="other-talks-row-box"
      >
        <Typography className="other-talks-title">Other Talks</Typography>


        {showLeftArrow && (
          <IconButton
            onClick={scrollCategoriesLeft}
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              width: 36,
              height: 36,
              "&:hover": {
                backgroundColor: "#222",
              },
            }}
          >
            {"<"}
          </IconButton>
        )}

        <Box
          ref={categoryScrollRef} sx={{ display: "flex", gap: 2, flexWrap: "nowrap", overflowX: "auto", alignItems: "center", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }} className="other-talks-cat-box">
          {categories.map((cat) => (
            <Button
              key={cat}
              onClick={() => {
                setSelectedCategory(cat);
                setActivePage(0);
              }}
              variant="contained"
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: 0,
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                opacity: selectedCategory === cat ? 1 : 0.6,
                whiteSpace: "nowrap",
                flexShrink: 0,
                "&:hover": { backgroundColor: "#000" },
              }}
            >
              {cat}
            </Button>
          ))}
          {isAdmin && (
            <Button
              onClick={handleUploadModalOpen}
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: 0,
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                opacity: 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
                minWidth: "180px",
                "&:hover": { backgroundColor: "#000" },
              }}
            >
              <img
                style={{ width: "20%", paddingRight: "10px" }}
                src={
                  process.env.PUBLIC_URL + "/Assets/IgniteTalks/upload_icon.png"
                }
                className="upload_icon"
              />
              Upload videos
            </Button>
          )}
        </Box>
        {showRightArrow && (
          <IconButton
            onClick={scrollCategoriesRight}
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              width: 36,
              height: 36,
              "&:hover": {
                backgroundColor: "#222",
              },
            }}
          >
            {">"}
          </IconButton>
        )}
      </Box>

      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} className="modal-root">
          <Box className="modal-header">
            <Typography className="close-btn" onClick={handleCloseModal}>
              Close ✕
            </Typography>
          </Box>

          <Box className="modal-body">
            {/* LEFT SECTION */}
            <Box className="left-section">
              <Typography className="section-title" variant="h1">
                {editingTalkId ? "Edit Ignite Talk" : "Upload Ignite Talks"}
              </Typography>

              <Box className="left-section-wrap">
                <Box className="form-row">
                  <Box sx={{ width: "100%" }}>
                    <label
                      htmlFor="speaker-name"
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Speaker name
                    </label>
                    <OutlinedInput
                      id="speaker-name"
                      placeholder="Type here..."
                      fullWidth
                      name="speakerName"
                      value={formData?.speakerName}
                      onChange={handleChange}
                      sx={{
                        borderRadius: "0",
                        height: "2rem",
                      }}
                    />
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <label
                      htmlFor="session-title"
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Session Title
                    </label>
                    <OutlinedInput
                      id="session-title"
                      placeholder="Type here..."
                      fullWidth
                      name="sessionTitle"
                      value={formData?.sessionTitle}
                      onChange={handleChange}
                      sx={{
                        borderRadius: "0",
                        height: "2rem",
                      }}
                    />
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <label
                        htmlFor="speaker-description"
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        Session description
                      </label>
                      <Typography
                        variant="small"
                        sx={{
                          fontSize: "12px",
                          textAlign: "end",
                          minWidth: "fit-content",
                        }}
                      >
                        Max 300 characters
                      </Typography>
                    </Box>
                    <OutlinedInput
                      id="speaker-description"
                      placeholder="Type here..."
                      fullWidth
                      name="sessionDescription"
                      value={formData.sessionDescription}
                      onChange={handleChange}
                      sx={{
                        borderRadius: "0",
                        height: "4rem",
                      }}
                    />
                  </Box>
                  <Box>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Category
                    </label>

                    <Select
                      fullWidth
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ height: "2rem", borderRadius: 0 }}
                    >
                      <MenuItem value="" disabled>
                        Select category
                      </MenuItem>
                      <MenuItem value="Digital Customer">
                        Digital Customer
                      </MenuItem>
                      <MenuItem value="Technology">Technology</MenuItem>
                      <MenuItem value="Design">Design</MenuItem>
                      <MenuItem value="Gen AI">Gen AI</MenuItem>
                      <MenuItem value="Success story">Success story</MenuItem>
                      <MenuItem value="Best practices">Best practices</MenuItem>
                      <MenuItem value="LCNC">LCNC</MenuItem>
                      <MenuItem value="Song Offerings">Song Offerings</MenuItem>
                    </Select>
                  </Box>
                </Box>
                <Box>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Upload speaker’s profile image
                  </label>

                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #ccc",
                      height: "8.1rem",
                      cursor: "pointer",
                    }}
                  >
                    {/* LEFT SIDE - UPLOAD */}
                    <Box
                      onDragOver={handleImageDragOver}
                      onDrop={handleSpeakerImageDrop}
                      onDragEnter={handleSpeakerDragEnter}
                      onDragLeave={handleSpeakerDragLeave}
                      sx={{
                        backgroundColor: speakerImageDragging
                          ? "#f5f5f5"
                          : "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        Drag and drop to upload
                      </Typography>

                      <CloudUploadIcon sx={{ fontSize: 30 }} />

                      <Button
                        variant="contained"
                        component="label"
                        disabled={!!editingTalkId}
                        sx={{
                          backgroundColor: "#000",
                          borderRadius: 0,
                          fontSize: "12px",
                          "&:hover": { backgroundColor: "#000" },
                        }}
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          disabled={!!editingTalkId}
                          onChange={(e) => handleImageUpload(e.target.files[0])}
                        />
                      </Button>
                    </Box>

                    {/* RIGHT SIDE - PREVIEW */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      {imagePreview ? (
                        <Avatar
                          src={imagePreview}
                          sx={{ width: "100%", height: "100%" }}
                        />
                      ) : (
                        <>
                          <Avatar sx={{ width: 50, height: 50 }} />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#666",
                              textAlign: "center",
                            }}
                          >
                            No files have been uploaded
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="form-row-bottom">
                <Box>
                  <label
                    htmlFor="session-duration"
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Session Duration (Minutes)
                  </label>
                  <OutlinedInput
                    id="session-duration"
                    placeholder="Type here..."
                    fullWidth
                    name="sessionDuration"
                    value={formData.sessionDuration}
                    onChange={handleChange}
                    sx={{
                      borderRadius: "0",
                      height: "2rem",
                    }}
                  />
                </Box>
                <Box>
                  <label
                    htmlFor="total-recordings"
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Total Recordings
                  </label>
                  <OutlinedInput
                    id="total-recordings"
                    placeholder="Type here..."
                    fullWidth
                    name="totalRecordings"
                    value={formData.totalRecordings}
                    onChange={handleChange}
                    disabled={!!editingTalkId}
                    sx={{
                      borderRadius: "0",
                      height: "2rem",
                    }}
                  />

                </Box>
                <Box>
                  <label
                    htmlFor="session-date"
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Session Date
                  </label>
                  <TextField
                    type="date"
                    name="sessionDate"
                    size="small"
                    value={formData.sessionDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Box>

              <hr className="divider" />

              <Typography className="section-subtitle">
                Upload session clips
              </Typography>

              <Box className="left-section-wrap">
                <Box className="form-row">
                  <Box sx={{ width: "100%" }}>
                    <label
                      htmlFor="clipTitle"
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Clip Title
                    </label>
                    <OutlinedInput
                      id="clipTitle"
                      placeholder="Enter here..."
                      fullWidth
                      name="clipTitle"
                      value={currentClip.clipTitle}
                      onChange={(e) => {
                        const value = e.target.value;

                        setCurrentClip((prev) => ({
                          ...prev,
                          clipTitle: value,
                        }));

                        if (editingTalkId) {
                          setVideos((prev) =>
                            prev.map((video, index) =>
                              index === selectedClipIndex
                                ? {
                                  ...video,
                                  clipTitle: value,
                                }
                                : video
                            )
                          );
                        }
                      }}
                      sx={{
                        borderRadius: "0",
                        height: "2rem",
                      }}
                    />
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <label
                        htmlFor="speaker-description"
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        Clip description
                      </label>
                      <Typography
                        variant="small"
                        sx={{
                          fontSize: "12px",
                          textAlign: "end",
                          minWidth: "fit-content",
                        }}
                      >
                        Max 100 characters
                      </Typography>
                    </Box>
                    <OutlinedInput
                      id="clipDescription"
                      placeholder="Enter here..."
                      fullWidth
                      name="clipDescription"
                      value={currentClip.clipDescription}
                      onChange={(e) => {
                        const value = e.target.value;

                        setCurrentClip((prev) => ({
                          ...prev,
                          clipDescription: value,
                        }));

                        if (editingTalkId) {
                          setVideos((prev) =>
                            prev.map((video, index) =>
                              index === selectedClipIndex
                                ? {
                                  ...video,
                                  clipDescription: value,
                                }
                                : video
                            )
                          );
                        }
                      }}

                      sx={{
                        borderRadius: "0",
                        height: "4rem",
                      }}
                    />
                  </Box>
                </Box>
                {!editingTalkId && (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: "5px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        Upload video
                      </label>

                      <Typography sx={{ fontSize: "12px" }}>
                        Max 10 Uploads
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        border: "1px solid #ccc",
                        height: "8.1rem",
                        cursor: "pointer",
                      }}
                    >
                      {/* LEFT SIDE - UPLOAD */}
                      <Box
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        sx={{
                          backgroundColor: dragging ? "#f5f5f5" : "#fafafa",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "5px",
                          borderRight: "1px solid #ccc",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "12px", textAlign: "center" }}
                        >
                          Drag and drop to upload
                        </Typography>

                        <CloudUploadIcon sx={{ fontSize: 30 }} />

                        <Button
                          variant="contained"
                          component="label"
                          disabled={!!editingTalkId}
                          sx={{
                            backgroundColor: "#000",
                            borderRadius: 0,
                            fontSize: "12px",
                            "&:hover": { backgroundColor: "#000" },
                          }}
                        >
                          Choose File
                          <input
                            id="video-upload"
                            type="file"
                            multiple
                            accept="video/*"
                            hidden
                            disabled={!!editingTalkId}
                            onChange={handleUpload}
                          />
                        </Button>
                      </Box>

                      {/* RIGHT SIDE - PREVIEW */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        {currentClip.file ? (
                          <>
                            <img
                              src={previewThumbnail || "/Assets/placeholder.png"}
                              alt="preview"
                              style={{
                                height: "50%",
                                objectFit: "cover",
                              }}
                            />
                            <Typography
                              sx={{ fontSize: "11px", textAlign: "center" }}
                            >
                              {currentClip?.file?.name}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <MovieEditIcon
                              sx={{ height: 50, width: 50, color: "#ccc" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#666",
                                textAlign: "center",
                              }}
                            >
                              No files have been uploaded
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
                {editingTalkId && (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: "5px",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        Video Preview (Read Only)
                      </label>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        border: "1px solid #ccc",
                        height: "8.1rem",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: "column",
                          width: "100%",
                          gap: "8px",
                        }}
                      >
                        {videos[selectedClipIndex]?.thumbnail ? (
                          <>
                            <img
                              src={videos[selectedClipIndex]?.thumbnail}
                              alt="preview"
                              style={{
                                height: "60%",
                                objectFit: "cover",
                              }}
                            />

                            <Typography
                              sx={{
                                fontSize: "11px",
                                textAlign: "center",
                              }}
                            >
                              {videos[selectedClipIndex]?.fileName}
                            </Typography>
                          </>
                        ) : (
                          <>
                            <MovieEditIcon
                              sx={{
                                height: 50,
                                width: 50,
                                color: "#ccc",
                              }}
                            />

                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#666",
                              }}
                            >
                              Preview unavailable
                            </Typography>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              {!editingTalkId && (
                <Box sx={{ width: "47%" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Upload clip thumbnail
                  </label>

                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #ccc",
                      height: "8.1rem",
                      cursor: "pointer",
                    }}
                  >
                    {/* LEFT SIDE - UPLOAD */}
                    <Box
                      onDragOver={handleImageDragOver}
                      onDrop={handleThumbnailDrop}
                      onDragEnter={handleThumbnailDragEnter}
                      onDragLeave={handleThumbnailDragLeave}
                      sx={{
                        backgroundColor: thumbnailDragging
                          ? "#f5f5f5"
                          : "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "5px",
                        borderRight: "1px solid #ccc",
                        width: "11rem",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", textAlign: "center" }}>
                        Drag and drop to upload
                      </Typography>

                      <CloudUploadIcon sx={{ fontSize: 30 }} />

                      <Button
                        variant="contained"
                        component="label"
                        disabled={!!editingTalkId}
                        sx={{
                          backgroundColor: "#000",
                          borderRadius: 0,
                          fontSize: "12px",
                          "&:hover": { backgroundColor: "#000" },
                        }}
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          disabled={!!editingTalkId}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;

                            const previewUrl = URL.createObjectURL(file);

                            setCurrentClip((prev) => ({
                              ...prev,
                              customThumbnail: previewUrl,
                              customThumbnailFile: file,
                            }));
                          }}
                        />
                      </Button>
                    </Box>

                    {/* RIGHT SIDE - PREVIEW */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "5px",
                        padding: "2rem",
                        width: "12rem",
                      }}
                    >
                      {previewThumbnail ? (
                        <>
                          <img
                            src={previewThumbnail || "/Assets/placeholder.png"}
                            alt="preview"
                            style={{
                              width: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <MovieEditIcon
                            sx={{ height: 50, width: 50, color: "#ccc" }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#666",
                              textAlign: "center",
                            }}
                          >
                            No files have been uploaded
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
              {editingTalkId && (
                <Box sx={{ width: "47%" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Thumbnail Preview (Read Only)
                  </label>

                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #ccc",
                      height: "8.1rem",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "10px",
                    }}
                  >
                    {videos[selectedClipIndex]?.thumbnail ? (
                      <img
                        src={videos[selectedClipIndex]?.thumbnail}
                        alt="thumbnail"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <>
                        <MovieEditIcon
                          sx={{
                            height: 50,
                            width: 50,
                            color: "#ccc",
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              )}

              <Box className="footer-btns">
                <Button
                  className="cancel-btn"
                  sx={{ color: "#a100ff" }}
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                {!editingTalkId && (
                  <Button
                    variant="outlined"
                    sx={{
                      width: "auto",
                      borderColor: "#a100ff",
                      color: "#a100ff",
                    }}
                    onClick={handleSaveDraft}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Save Draft"
                    )}
                  </Button>
                )}

                <Button
                  variant="contained"
                  className="upload-btn"
                  sx={{ width: "auto", backgroundColor: "#a100ff" }}
                  onClick={handleSubmit}
                  disabled={loader}
                >
                  {loader ? (
                    <CircularProgress size={20} />
                  ) : editingTalkId ? (
                    "Update"
                  ) : (
                    "Upload"
                  )}
                </Button>
              </Box>
            </Box>

            {/* RIGHT SECTION */}
            <Box className="right-section">
              <Box className="right-header">
                <Typography>Uploaded video files</Typography>
                <Typography>Total uploads {videos?.length}</Typography>
              </Box>

              {videos.map((video, index) => (
                <Box
                  key={index}
                  className="video-card"
                  onClick={() => {
                    setSelectedClipIndex(index);
                    setEditingClipIndex(index);

                    setCurrentClip({
                      file: video.file || null,
                      duration: video.duration ?? null,
                      autoThumbnail: null,
                      customThumbnail: video.thumbnail || null,
                      customThumbnailFile: video.thumbnailFile || null,
                      clipTitle: video.clipTitle || "",
                      clipDescription: video.clipDescription || "",
                      videoUrl: video.videoUrl || "",
                      thumbnailUrl:
                        video.thumbnailUrl ||
                        (
                          video.thumbnail?.startsWith("http")
                            ? video.thumbnail
                            : ""
                        ),
                    });
                  }}

                  sx={{
                    cursor: "pointer",
                    backgroundColor:
                      selectedClipIndex === index
                        ? "#A100FF"
                        : "#ffffff",
                    color:
                      selectedClipIndex === index
                        ? "#fff"
                        : "inherit",
                    transition: "all 0.2s ease-in-out",
                    border: "1px solid #e0e0e0",
                    padding: "10px",
                    borderRadius: "8px",
                  }}
                >

                  <Box className="video-info">
                    <Typography className="video-title">
                      <b>{video?.clipTitle || video?.file?.name}</b>
                    </Typography>

                    <Typography className="video-title">
                      {video?.clipDescription}
                    </Typography>

                    <Box className="video-meta">
                      <AccessTimeIcon />
                      {formatDuration(video?.duration)} mins
                    </Box>
                  </Box>

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();

                      setSelectedClipIndex(index);
                      setEditingClipIndex(index);

                      setCurrentClip({
                        file: video.file || null,
                        duration: video.duration ?? null,
                        autoThumbnail: null,
                        customThumbnail:
                          video.thumbnail || null,
                        customThumbnailFile:
                          video.thumbnailFile || null,
                        clipTitle:
                          video.clipTitle || "",
                        clipDescription:
                          video.clipDescription || "",
                        videoUrl:
                          video.videoUrl || "",
                        thumbnailUrl:
                          video.thumbnailUrl ||
                          (
                            video.thumbnail?.startsWith("http")
                              ? video.thumbnail
                              : ""
                          ),
                      });
                    }}
                    sx={{
                      color: "#A100FF",
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          Delete Talk
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this talk?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedTalkId(null);
            }}
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={() => handleDeleteTalk(selectedTalkId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>

      {otherTalks.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#555" }}>
            No Ignite Talks available yet.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: `${GAP}px`,
          pb: 3,
        }}
      >
        {paginatedTalks.map((talk, index) => {
          // const latestDate = Math.max(
          //   ...otherTalks.map((t) => new Date(t.courseDate))
          // );

          // const isLatest = new Date(talk.courseDate).getTime() === latestDate;
          const rawThumb = talk.episodes?.[0]?.thumbnail;

          const thumb = !rawThumb
            ? "/Assets/placeholder.png"
            : rawThumb.startsWith("http") || rawThumb.startsWith("data:")
              ? rawThumb
              : imageUrls?.[rawThumb] || "/Assets/placeholder.png";

          const speakerImg = !talk.speakerImage
            ? null
            : talk.speakerImage.startsWith("http") ||
              talk.speakerImage.startsWith("data:")
              ? talk.speakerImage
              : imageUrls?.[talk.speakerImage];

          const latestTalkId = talks.length
            ? talks.reduce((latest, talk) => {
              if (!latest) return talk;

              const latestDate = latest.courseDate
                ? new Date(latest.courseDate).getTime()
                : 0;

              const currentDate = talk.courseDate
                ? new Date(talk.courseDate).getTime()
                : 0;

              return currentDate > latestDate ? talk : latest;
            }, null)?.id
            : null;

          return (
            <Box
              key={talk.id}
              onClick={() => onSelectTalk(talk)}
              sx={{
                cursor: "pointer",
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                boxShadow:
                  "0 4px 8px rgba(0,0,0,0.12), 0 6px 10px rgba(0,0,0,0.14)",
              }}
            >
              <Box sx={{ position: "relative" }}>
                {talk.id === latestTalkId && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 0,
                      backgroundColor: "#224BFF",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 700,
                      px: 1,
                      py: "2px",
                      zIndex: 1,
                    }}
                  >
                    NEW
                  </Box>
                )}

                {isAdmin && !open && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(talk.id);
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      backgroundColor: "rgba(0,0,0,0.7)",
                      color: "#fff",
                      zIndex: 1,
                      "&:hover": {
                        backgroundColor: "#d32f2f",
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
                <Box
                  component="img"
                  src={thumb}
                  alt={talk.title}
                  sx={{ width: "100%", height: 170, objectFit: "cover" }}
                  style={{ filter: "brightness(0.5)" }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      backgroundColor: "#A100FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderLeft: "12px solid #fff",
                        ml: "3px",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#A100FF",
                      lineHeight: 1.4,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      flex: 1,
                    }}
                  >
                    {talk?.title}
                  </Typography>

                  {isAdmin && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(talk);
                      }}
                      sx={{
                        color: "#A100FF",
                        p: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }}
                >
                  <Typography sx={{ fontSize: 12 }}>Speaker:</Typography>
                  <Avatar
                    src={speakerImg || "/Assets/avatar.png"}
                    sx={{ width: 18, height: 18 }}
                  />
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    {talk?.speakerName
                      ?.toLowerCase()
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#666",
                    lineHeight: 1.5,
                    mb: 2,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {talk?.courseDescription}
                </Typography>

                <Box
                  sx={{
                    mt: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "#666",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    {talk?.duration} mins
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <EventIcon sx={{ fontSize: 16 }} />
                    {formatDate(talk?.courseDate)}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {pageCount > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mt: 2,
            mb: 2,
          }}
          className="scrollable-option"
        >
          {Array.from({ length: pageCount }).map((_, index) => (
            <Box
              key={index}
              onClick={() => setActivePage(index)}
              sx={{
                width: activePage === index ? 22 : 8,
                height: 8,
                borderRadius: 0,
                backgroundColor: activePage === index ? "#000" : "#bbb",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OtherTalksSection;
